<template>
  <div class="container mx-auto px-4 py-8">
    <h1 class="text-3xl font-bold mb-6">Test API Connection</h1>

    <div class="space-y-6">
      <!-- Test de santé système -->
      <div class="bg-white p-6 rounded-lg shadow">
        <h2 class="text-xl font-semibold mb-4">System Health</h2>
        <button
          @click="testSystemHealth"
          :disabled="loading.system"
          class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {{ loading.system ? 'Testing...' : 'Test System Health' }}
        </button>
        <div v-if="results.system" class="mt-4">
          <pre class="bg-gray-100 p-4 rounded text-sm">{{
            JSON.stringify(results.system, null, 2)
          }}</pre>
        </div>
      </div>

      <!-- Test liste graffitis -->
      <div class="bg-white p-6 rounded-lg shadow">
        <h2 class="text-xl font-semibold mb-4">Graffitis List</h2>
        <button
          @click="testGraffitisList"
          :disabled="loading.graffitis"
          class="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:opacity-50"
        >
          {{ loading.graffitis ? 'Loading...' : 'Load Graffitis (first 5)' }}
        </button>
        <div v-if="results.graffitis" class="mt-4">
          <p class="mb-2">Loaded {{ results.graffitis.length }} graffitis:</p>
          <div class="space-y-2">
            <div
              v-for="graffiti in results.graffitis"
              :key="graffiti.idGraf"
              class="bg-gray-50 p-3 rounded"
            >
              <strong>{{ graffiti.idBdc }}</strong> - {{ graffiti.type }} -
              {{ graffiti.categorie }}
              <br />
              <small class="text-gray-600">Themes: {{ graffiti.themes.join(', ') }}</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Test graffiti individuel -->
      <div class="bg-white p-6 rounded-lg shadow">
        <h2 class="text-xl font-semibold mb-4">Individual Graffiti</h2>
        <div class="flex gap-2 mb-4">
          <input
            v-model="graffitiId"
            type="number"
            placeholder="Graffiti ID"
            class="border rounded px-3 py-2"
          />
          <button
            @click="testGraffitiById"
            :disabled="loading.individual || !graffitiId"
            class="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 disabled:opacity-50"
          >
            {{ loading.individual ? 'Loading...' : 'Load Graffiti' }}
          </button>
        </div>
        <div v-if="results.individual" class="mt-4">
          <pre class="bg-gray-100 p-4 rounded text-sm max-h-96 overflow-y-auto">{{
            JSON.stringify(results.individual, null, 2)
          }}</pre>
        </div>
      </div>

      <!-- Erreurs -->
      <div v-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <strong>Error:</strong> {{ error }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { api } from '@/api'
import type { HealthCheckResponse } from '@/types/api'
import type { GraffitiFromAPI } from '@/types/entities'
import { ref } from 'vue'

interface TestApiResults {
  system: HealthCheckResponse | null
  graffitis: GraffitiFromAPI[] | null
  individual: GraffitiFromAPI | null
}

// État
const loading = ref({
  system: false,
  graffitis: false,
  individual: false,
})

const results = ref<TestApiResults>({
  system: null,
  graffitis: null,
  individual: null,
})

const error = ref<string | null>(null)
const graffitiId = ref(1)

// Test de santé système
async function testSystemHealth() {
  loading.value.system = true
  error.value = ''
  try {
    const result = await api.system.healthCheck()
    results.value.system = result
    console.log('[TestAPI] System health:', result)
  } catch (err) {
    error.value = `System health failed: ${err}`
    console.error('[TestAPI] System health error:', err)
  } finally {
    loading.value.system = false
  }
}

// Test liste graffitis
async function testGraffitisList() {
  loading.value.graffitis = true
  error.value = ''
  try {
    const result = await api.graffitis.list({ page: 1, limit: 5 })
    results.value.graffitis = result.data
    console.log('[TestAPI] Graffitis list:', result)
  } catch (err) {
    error.value = `Graffitis list failed: ${err}`
    console.error('[TestAPI] Graffitis list error:', err)
  } finally {
    loading.value.graffitis = false
  }
}

// Test graffiti individuel
async function testGraffitiById() {
  loading.value.individual = true
  error.value = ''
  try {
    const result = await api.graffitis.getById(graffitiId.value)
    results.value.individual = result
    console.log('[TestAPI] Individual graffiti:', result)
  } catch (err) {
    error.value = `Individual graffiti failed: ${err instanceof Error ? err.message : String(err)}`
    console.error('[TestAPI] Individual graffiti error:', err)
  } finally {
    loading.value.individual = false
  }
}
</script>
